import classNames from 'classnames';
import * as React from 'react';
import strings from 'owa-strings/owa-extensibility-strings';
import TaskPaneError from './TaskPaneError';
import TaskPaneHeader from './TaskPaneHeader';
import { AddinCommandsManifestCacheProvider } from 'owa-addins-osf-facade';
import { AddinPanel } from './AddinPanel';
import { closeTaskPaneAddinCommandByControlId } from 'owa-addins-apis';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { format } from 'owa-strings';
import { isDeepLink } from 'owa-popout';
import { observer } from 'mobx-react';
import { OsfHostContainer } from '../components/OsfHostContainer';
import 'owa-addins-osfruntime';
import {
    AddinCommand,
    TaskPaneRunningInstance,
    TaskPaneType,
    TaskPaneErrorType,
    toggleTaskPaneType,
} from 'owa-addins-store';
import {
    setPersistedAddin,
    addinSupportsPersistence,
    createPersistedAddin,
} from 'owa-addins-persistent';

const styles = require('./TaskPane.scss');

export interface TaskPaneProps {
    type: TaskPaneType;
    instance: TaskPaneRunningInstance;
    renderInPanel?: boolean;
}

@observer
export default class TaskPane extends React.Component<TaskPaneProps, {}> {
    private taskPaneHeader: TaskPaneHeader;

    render() {
        const { instance } = this.props;

        const renderInPanel =
            this.props.renderInPanel === undefined ? true : this.props.renderInPanel;

        if (!instance) {
            return <div className={styles.divContainer} />;
        }

        const { addinCommand } = instance;

        return (
            <AddinPanel
                renderInPanel={renderInPanel}
                onDismiss={this.close}
                isDeepLink={isDeepLink()}
                disableFirstFocus={this.isPersisted()}>
                <TaskPaneHeader
                    iconUrl={addinCommand.extension.IconUrl}
                    displayName={addinCommand.extension.DisplayName}
                    closeOnClick={this.close}
                    ref={ref => (this.taskPaneHeader = ref)}
                    isCalendar={this.isCalendar()}
                    isPersistable={this.supportsPersisted()}
                    isPersisted={this.isPersisted()}
                    togglePersist={this.togglePersist}
                />
                <div
                    tabIndex={0}
                    className={classNames(
                        styles.extensionContainer,
                        this.isCalendar() && styles.extensionContainerCalendar
                    )}
                    aria-label={format(
                        strings.extensionContainerAriaLabel,
                        addinCommand.extension.DisplayName
                    )}
                    role="complementary">
                    {this.renderError()}
                    {this.renderOsfHostContainer()}
                </div>
            </AddinPanel>
        );
    }

    private focusTaskPaneHeader = (): void => {
        if (this.taskPaneHeader) {
            this.taskPaneHeader.focusCloseButton();
        }
    };

    private renderError = (): JSX.Element => {
        const { errorType } = this.props.instance;
        return this.hasError() && <TaskPaneError errorType={errorType} />;
    };

    private renderOsfHostContainer = (): JSX.Element => {
        const { hostItemIndex, controlId, addinCommand } = this.props.instance;

        const notifyHostActions = {};
        notifyHostActions[OSF.AgaveHostAction.EscExit] = this.focusTaskPaneHeader;
        notifyHostActions[OSF.AgaveHostAction.TabExitShift] = this.focusTaskPaneHeader;
        notifyHostActions[OSF.AgaveHostAction.TabExit] = this.focusTaskPaneHeader;

        return (
            <OsfHostContainer
                addinCommand={addinCommand}
                controlId={controlId}
                className={classNames([styles.extensionHost, this.hasError() && styles.hide])}
                hostItemIndex={hostItemIndex}
                manifestCacheProvider={AddinCommandsManifestCacheProvider}
                notifyHostActions={notifyHostActions}
            />
        );
    };

    private supportsPersisted() {
        return addinSupportsPersistence(this.props.instance.addinCommand as AddinCommand);
    }

    private isCalendar() {
        return (
            this.props.instance.mode === ExtensibilityModeEnum.AppointmentAttendee ||
            this.props.instance.mode === ExtensibilityModeEnum.AppointmentOrganizer
        );
    }

    private isPersisted() {
        return this.props.type === TaskPaneType.Persistent;
    }

    private hasError = (): boolean => {
        const { errorType } = this.props.instance;
        return errorType !== TaskPaneErrorType.None;
    };

    private close = (): void => {
        const { mode, controlId } = this.props.instance;

        if (this.isPersisted()) {
            setPersistedAddin(mode, null /* PersistedAddinCommand */);
        }

        closeTaskPaneAddinCommandByControlId(controlId);
    };

    private togglePersist = (): void => {
        const addin = this.isPersisted()
            ? null
            : createPersistedAddin(this.props.instance.addinCommand as AddinCommand);
        setPersistedAddin(this.props.instance.mode, addin);

        toggleTaskPaneType(this.props.type);
    };
}
